// src/components/main/GiftCardGenerator.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  useTheme,
} from '@mui/material';
import ShopperStripe from '../payments/ShopperStripe';
import GiftCardPdf from '../giftcards/GiftCardPdf';

function generateCode(len = 12) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: len }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join('');
}

export default function GiftCardGenerator() {
  const theme = useTheme();
  const amounts = [50, 75, 100, 150];

  const [amount, setAmount] = useState(null);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');

  const [stage, setStage] = useState('select'); // select | pay | result | failed
  const [giftCard, setGiftCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const amountNumber = Number(amount || 0);

  const startPayment = () => {
    if (!amount || !buyerName || !buyerEmail || !recipientName || !recipientEmail) {
      setError('Please fill out all required fields.');
      return;
    }
    setError('');
    setStage('pay');
  };

  const handleReset = () => {
    setAmount(null);
    setBuyerName('');
    setBuyerEmail('');
    setRecipientName('');
    setRecipientEmail('');
    setGiftCard(null);
    setError('');
    setStage('select');
  };

  const handlePaymentSuccess = async (pi) => {
    setLoading(true);
    const code = generateCode();
    const issuedAt = Date.now();

    try {
      // 1) register gift card
      await fetch('/giftcards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, amount: amountNumber, issuedAt }),
      });

      // 2) record order
      await fetch('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: pi.id,
          email: buyerEmail,
          items: [{ name: `Gift‑card €${amount}`, qty: 1, subtotal: amountNumber }],
          total: amountNumber,
          createdAt: issuedAt,
          code,                            
        }),
      });

      // 3) send receipt
      await fetch('/orders/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: pi.id, to: buyerEmail }),
      });

      setGiftCard({
        code,
        amount,
        piDetails: pi,                     // ← Stripe PaymentIntent
        issuedAt,
        issuedAtFormatted: new Date(issuedAt).toLocaleDateString(),
      });
      setStage('result');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Something went wrong');
      setStage('failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentFailed = (err) => {
    console.error(err);
    setError('Payment failed, please try again.');
    setStage('failed');
  };

  const handleEmailGiftCard = async () => {
    setLoading(true);
    try {
      await fetch('/giftcards/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: giftCard.code,
          to: recipientEmail,
          amount: amountNumber,
          issuedAt: giftCard.issuedAt,
          fromName: buyerName,
          toName: recipientName,
        }),
      });
      alert(`Gift card emailed to ${recipientEmail}`);
    } catch {
      alert('Failed to email gift card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      id="giftcard-generator"
      sx={{ py: { xs: 6, md: 10 }, bgcolor: theme.palette.background.default }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center'}}>
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
          Create a Gift Card
        </Typography>

        {stage === 'select' && (
          <>
            {/* amount buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mb: 4,
                flexWrap: 'wrap',
              }}
            >
              {amounts.map((a) => (
                <Button
                  key={a}
                  onClick={() => setAmount(a)}
                  sx={{
                    minWidth: 80,
                    height: 80,
                    borderRadius: 2,
                    border: `2px solid ${theme.palette.grey[400]}`,
                    bgcolor: amount === a ? theme.palette.secondary.light : 'transparent',
                    borderColor: amount === a ? theme.palette.secondary.main : undefined,
                    color: amount === a ? theme.palette.common.white : undefined,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                  }}
                >
                  €{a}
                </Button>
              ))}
            </Box>

          {/* form fields: flex two‑per‑row */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
                justifyContent: 'center',
              }}
            >
             {[
                { label: 'Your Name ', value: buyerName, onChange: (e) => setBuyerName(e.target.value) },
                { label: 'Your Email ', type: 'email', value: buyerEmail, onChange: (e) => setBuyerEmail(e.target.value) },
                { label: 'Recipient Name ', value: recipientName, onChange: (e) => setRecipientName(e.target.value) },
                { label: 'Recipient Email ', type: 'email', value: recipientEmail, onChange: (e) => setRecipientEmail(e.target.value) },
              ].map((field) => (
                <TextField
                  key={field.label}
                  required
                  fullWidth={false}
                  type={field.type || 'text'}
                  label={field.label}
                  value={field.value}
                  onChange={field.onChange}
                  sx={{
                    flex: '1 1 calc(50% - 8px)',
                    // ensure they're at least half the container minus gap
                  }}
                />
              ))}
            </Box>

            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={startPayment}
              disabled={
                !amount ||
                !buyerName ||
                !buyerEmail ||
                !recipientName ||
                !recipientEmail
              }
              fullWidth
              sx={{
                py: 1.5,
                fontFamily: '"Playfair Display", serif',
                backgroundColor: theme.palette.grey[900],
                color: theme.palette.common.white,
                '&:hover': { backgroundColor: theme.palette.grey[800] },
              }}
            >
              Continue to Payment
            </Button>
          </>
        )}

        {stage === 'pay' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pay €{amountNumber.toFixed(2)}
            </Typography>
            <ShopperStripe
              amount={amountNumber}
              skipOrder
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentFailed}
            />
            <Button
              onClick={handleReset}
              sx={{
                mt: 2,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Cancel
            </Button>
          </Box>
        )}

        {stage === 'failed' && (
          <Box sx={{ mt: 4 }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setStage('pay')}
              sx={{ mr: 2 }}
            >
              Retry Payment
            </Button>
            <Button onClick={handleReset}>Start Over</Button>
          </Box>
        )}

        {stage === 'result' && giftCard && (
  <Box sx={{ mt: 4 }}>
    <Card sx={{ textAlign: 'left' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Your Gift Card
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Code:</strong> {giftCard.code}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Value:</strong> €{giftCard.amount}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Issued on {giftCard.issuedAtFormatted}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, gap: 2 }}>
        {/* Download PDF */}
        <GiftCardPdf
          code={giftCard.code}
          amount={Number(giftCard.amount)}
          issuedAt={giftCard.issuedAt}
          toName={recipientEmail}
          fromName={buyerName}
        />

        {/* Email to recipient */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleEmailGiftCard}
          disabled={loading}
          sx={{ flex: 1, py: 1.5 }}
        >
          {loading
            ? <CircularProgress size={20} color="inherit" />
            : 'Email to Recipient'
          }
        </Button>
      </CardActions>
    </Card>
  </Box>
)}

      </Container>
    </Box>
  );
}
